import {sleep} from '@benchling/js-utils';

import DocumentActions from 'notebook/documents/DocumentActions';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeProcedureEventMetadataFromFlowchart,
  composeProcedureTypeMetadata,
  PROCEDURE_SAVE_ENDED_EVENT,
  PROCEDURE_SAVE_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import {ProcedureFlowchart, ProcedureMetadata, ProcedureWithMetadata} from 'procedures/ProcedureDomainTypes';
import {PersistenceInfo} from 'procedures/ProcedurePageContext';
import {
  getDocumentVersionUpdates,
  syncPersistedDataIntoLocalFlowchart,
  syncPersistedIdsIntoLocalFlowchart,
} from 'procedures/procedurePagePersistenceUtils';
import {setIsDirty as setProcedureDirty} from 'procedures/redux/proceduresAutosaveManagerCallbacks';

type SaveHandler = (
  procedure: ProcedureWithMetadata,
  isBlocking?: boolean,
  persistenceInfo?: PersistenceInfo
) => Promise<null | ProcedureMetadata>;

type SetPendingDraftFlowchartUpdate = null | {
  updater: (fc: ProcedureFlowchart) => ProcedureFlowchart;
};

/**
 * This factory for making a saveHandler is used to ensure that the saveHandler is protected from
 * unmounted components.
 *
 * Callers must ensure that the callbacks are noops if the component is unmounted. They are only provided
 * to allow components to do bookkeeping, which isn't needed once the component is unmounted.
 *
 * The factory returns a saveHandler that can be passed to the AutosaveManager.
 */
export function saveHandlerFactory(
  setProcedureMetadata: (m: ProcedureMetadata) => void,
  updateAndPersistProcedure: (
    procedure: ProcedureWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>,
  setPendingDraftFlowchartUpdate: (newUpdate: SetPendingDraftFlowchartUpdate) => void,
  // TODO: This will be moved into ProcedureMetadata, so it won't need to be included here separately
  processFlowchartConfigId: string | null = null
): SaveHandler {
  return (procedure: ProcedureWithMetadata, isBlocking?: boolean, persistenceInfo?: PersistenceInfo) =>
    saveHandler(
      procedure,
      isBlocking,
      persistenceInfo,
      setProcedureMetadata,
      updateAndPersistProcedure,
      setPendingDraftFlowchartUpdate,
      processFlowchartConfigId
    );
}

async function saveHandler(
  // --- Provided by the caller (i.e., the AutosaveManager)
  procedure: ProcedureWithMetadata,
  isBlocking: boolean = false,
  persistenceInfo: PersistenceInfo | undefined = undefined,
  // --- Provided by the factory
  setProcedureMetadata: (m: ProcedureMetadata) => void,
  updateAndPersistProcedure: (
    procedure: ProcedureWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>,
  setPendingDraftFlowchartUpdate: (newUpdate: SetPendingDraftFlowchartUpdate) => void,
  // TODO: This will be moved into ProcedureMetadata, so it won't need to be included here separately
  processFlowchartConfigId: string | null = null
): Promise<ProcedureMetadata | null> {
  const {metadata: procedureMetadata} = procedure;
  setProcedureDirty(procedureMetadata.id, false);

  const eventMetadata = composeProcedureTypeMetadata(procedureMetadata.procedureType);
  logProfilerTimingEvent(PROCEDURE_SAVE_STARTED_EVENT, eventMetadata);

  const documentVersionUpdates = getDocumentVersionUpdates(procedure.flowchart);
  DocumentActions.notifySaveStart(documentVersionUpdates);
  try {
    setProcedureMetadata(procedureMetadata);
    await sleep(0); // Ensure that the metadata update is flushed
    const result = await updateAndPersistProcedure({...procedure, processFlowchartConfigId}, persistenceInfo);
    DocumentActions.notifySaveComplete(documentVersionUpdates, result?.cidToIdMap ?? {});

    if (result != null) {
      setPendingDraftFlowchartUpdate({
        updater: (latestDraftFlowchart: ProcedureFlowchart) => {
          const idSyncedDraftFlowchart = syncPersistedIdsIntoLocalFlowchart(
            latestDraftFlowchart,
            result.cidToIdMap
          );
          return syncPersistedDataIntoLocalFlowchart(
            result.updatedProcedure,
            idSyncedDraftFlowchart,
            documentVersionUpdates
          );
        },
      });
    }
    return result ? result.updatedProcedure.metadata : null;
  } catch (e) {
    setProcedureDirty(procedureMetadata.id, true);
    throw e;
  } finally {
    const eventMetadata = composeProcedureEventMetadataFromFlowchart(
      Boolean(isBlocking),
      procedureMetadata.procedureType,
      procedure.flowchart,
      persistenceInfo
    );
    logProfilerTimingEvent(PROCEDURE_SAVE_ENDED_EVENT, eventMetadata);
  }
}
